import { Set } from 'immutable';

interface IRealtimeError {
  code: Codes;
  detail: string;
}

enum Codes {
  BadRequest = 'Error.BadRequest', // 400-like
  InvalidToken = 'Error.InvalidToken', // Bad or expired authentication token
  Forbidden = 'Error.Forbidden', // 403-like
  VersionTooOld = 'Error.VersionTooOld', // Client too far out of date
  InternalError = 'Error.Internal', // 500-like
  InsufficientQuota = 'Error.InsufficientQuota', // Action stream has exceeded quota
  NoSuchPeer = 'Error.NoSuchPeer', // Nonexistent or disconnected peer
  PeerErrorAck = 'Error.PeerErrorAck', // Message recipient acked with an error
  PeerInvalidAck = 'Error.PeerInvalidAck', // Could not parse message recipient ack
  PeerAckTimeout = 'Error.PeerAckTimeout', // Message recipient did not ack in time
  Subscription = 'Error.Subscription', // The client was already subscribed to one of the requested streams.

  // TODO: Legacy errors, remove once PR#356 on Realtime is out
  StreamPermissions = 'Error.StreamPermissions',
}
const CodeValues = Set(Object.values(Codes));

const NON_WRITABLE_ERRORS = Set([
  Codes.StreamPermissions,
  Codes.Forbidden,
  Codes.InsufficientQuota,
]);

function isRealtimeError(err: any): err is IRealtimeError {
  try {
    const { code, detail } = err as IRealtimeError;
    return CodeValues.has(code) && detail !== undefined;
  } catch (e) {
    return false;
  }
}

class RealtimeError extends Error {
  public static Codes = Codes;

  public static tryParse(error: any): RealtimeError | null {
    if (isRealtimeError(error)) {
      return new RealtimeError(error);
    }
    return null;
  }

  public readonly code: Codes;
  public readonly message: string;

  public constructor(error: IRealtimeError) {
    super();
    Object.setPrototypeOf(this, RealtimeError.prototype);
    this.code = error.code;
    this.message = error.detail;
  }

  public shouldRetry(): boolean {
    // Currently no known errors that would succeed on retry
    return false;
  }

  public shouldDropPending(): boolean {
    return NON_WRITABLE_ERRORS.has(this.code);
  }
}

export default RealtimeError;
